import React, { useState, useEffect, useCallback } from "react";
import { api, apiError } from "@/lib/api";
import { ROL_LABEL } from "@/lib/constants";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Plus, Pencil, KeyRound, Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import { t } from "@/lib/i18n";

const ROLES = ["admin", "dispatch", "mantenimiento", "chofer"];

export default function Usuarios() {
  const [usuarios, setUsuarios] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [resetTarget, setResetTarget] = useState(null);

  const cargar = useCallback(async () => {
    const { data } = await api.get("/usuarios");
    setUsuarios(data);
  }, []);

  useEffect(() => { cargar(); }, [cargar]);

  return (
    <div className="p-4 lg:p-8 space-y-5">
      <div className="flex items-center justify-between">
        <h1 className="font-display font-bold text-[26px] lg:text-[30px] leading-[1.05] text-ink" data-testid="page-title">{t("Usuarios")}</h1>
        <Button data-testid="usuario-nuevo-btn" className="bg-brand hover:bg-brand-dark text-white" onClick={() => { setEditing(null); setOpen(true); }}><Plus className="h-4 w-4 mr-2" />{t(" Nuevo usuario")}</Button>
      </div>

      <div className="bg-white rounded-xl border overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-surface-2 text-left eyebrow">
                <th className="px-4 py-3">{t("Nombre")}</th>
                <th className="px-4 py-3">{t("Usuario")}</th>
                <th className="px-4 py-3">{t("Rol")}</th>
                <th className="px-4 py-3">{t("Teléfono")}</th>
                <th className="px-4 py-3">{t("Camión")}</th>
                <th className="px-4 py-3">{t("Estado")}</th>
                <th className="px-4 py-3 text-right">{t("Acciones")}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#EFECE6]">
              {usuarios.map((u) => (
                <tr key={u.id} data-testid={`usuario-fila-${u.id}`} className={cn("hover:bg-surface-2", !u.activo && "opacity-50")}>
                  <td className="px-4 py-3 font-semibold text-ink">{u.nombre}</td>
                  <td className="px-4 py-3 font-mono text-ink-2">{u.usuario}</td>
                  <td className="px-4 py-3"><span className="inline-flex rounded-md bg-surface-2 border border-line px-2 py-1 text-[13px] font-medium text-ink-2">{ROL_LABEL[u.rol]}</span></td>
                  <td className="px-4 py-3 text-ink-2">{u.telefono || "—"}</td>
                  <td className="px-4 py-3 text-ink-2">{u.camion || "—"}</td>
                  <td className="px-4 py-3">{u.activo ? <span className="text-[#277A3F] font-medium">{t("Activo")}</span> : <span className="text-ink-3">{t("Inactivo")}</span>}</td>
                  <td className="px-4 py-3 text-right space-x-1">
                    <Button data-testid={`usuario-editar-${u.id}`} variant="ghost" size="sm" onClick={() => { setEditing(u); setOpen(true); }}><Pencil className="h-4 w-4" /></Button>
                    <Button data-testid={`usuario-reset-${u.id}`} variant="ghost" size="sm" onClick={() => setResetTarget(u)}><KeyRound className="h-4 w-4" /></Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {open && <UsuarioDialog usuario={editing} onClose={() => setOpen(false)} onSaved={() => { setOpen(false); cargar(); }} />}
      {resetTarget && <ResetDialog usuario={resetTarget} onClose={() => setResetTarget(null)} />}
    </div>
  );
}

function UsuarioDialog({ usuario, onClose, onSaved }) {
  const esEdicion = !!usuario;
  const [nombre, setNombre] = useState(usuario?.nombre || "");
  const [user, setUser] = useState(usuario?.usuario || "");
  const [password, setPassword] = useState("");
  const [rol, setRol] = useState(usuario?.rol || "chofer");
  const [telefono, setTelefono] = useState(usuario?.telefono || "");
  const [camion, setCamion] = useState(usuario?.camion || "");
  const [activo, setActivo] = useState(usuario ? usuario.activo : true);
  const [saving, setSaving] = useState(false);

  const guardar = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (esEdicion) {
        await api.put(`/usuarios/${usuario.id}`, { nombre, rol, telefono: telefono || null, camion: camion || null, activo });
      } else {
        await api.post("/usuarios", { nombre, usuario: user.trim().toLowerCase(), password, rol, telefono: telefono || null, camion: camion || null, activo });
      }
      toast.success(esEdicion ? t("Usuario actualizado") : t("Usuario creado"));
      onSaved();
    } catch (err) { toast.error(apiError(err)); } finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="bg-white">
        <DialogHeader><DialogTitle>{esEdicion ? t("Editar usuario") : t("Nuevo usuario")}</DialogTitle></DialogHeader>
        <form onSubmit={guardar} className="space-y-4">
          <div><Label className="eyebrow">{t("Nombre *")}</Label><Input data-testid="usuario-nombre-input" value={nombre} onChange={(e) => setNombre(e.target.value)} className="mt-1 h-11" required /></div>
          {!esEdicion && (
            <>
              <div><Label className="eyebrow">{t("Usuario *")}</Label><Input data-testid="usuario-usuario-input" value={user} onChange={(e) => setUser(e.target.value)} autoCapitalize="none" className="mt-1 h-11 font-mono" required /></div>
              <div><Label className="eyebrow">{t("Contraseña inicial *")}</Label><Input data-testid="usuario-password-input" type="text" value={password} onChange={(e) => setPassword(e.target.value)} className="mt-1 h-11" required /></div>
            </>
          )}
          <div>
            <Label className="eyebrow">{t("Rol *")}</Label>
            <Select value={rol} onValueChange={setRol}>
              <SelectTrigger data-testid="usuario-rol-select" className="h-11 bg-white mt-1"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-white">
                {ROLES.map((r) => <SelectItem key={r} value={r}>{ROL_LABEL[r]}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div><Label className="eyebrow">{t("Teléfono (+1XXXXXXXXXX)")}</Label><Input data-testid="usuario-telefono-input" value={telefono} onChange={(e) => setTelefono(e.target.value)} placeholder="+1..." className="mt-1 h-11" /></div>
          <div><Label className="eyebrow">{t("Camión")}</Label><Input data-testid="usuario-camion-input" value={camion} onChange={(e) => setCamion(e.target.value)} placeholder={t("Ej: Unidad 3")} className="mt-1 h-11" /></div>
          <label className="flex items-center gap-2 text-sm text-ink-2"><input data-testid="usuario-activo-check" type="checkbox" checked={activo} onChange={(e) => setActivo(e.target.checked)} className="h-4 w-4" />{t(" Activo")}</label>
          <DialogFooter className="gap-2">
            <Button type="button" variant="outline" onClick={onClose}>{t("Cancelar")}</Button>
            <Button type="submit" data-testid="usuario-guardar" className="bg-brand hover:bg-brand-dark text-white" disabled={saving}>{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : t("Guardar")}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function ResetDialog({ usuario, onClose }) {
  const [password, setPassword] = useState("");
  const [saving, setSaving] = useState(false);

  const guardar = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post(`/usuarios/${usuario.id}/reset-password`, { password });
      toast.success(t("Contraseña de {nombre} actualizada", { nombre: usuario.nombre }));
      onClose();
    } catch (err) { toast.error(apiError(err)); } finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="bg-white">
        <DialogHeader><DialogTitle>{t("Resetear contraseña — {nombre}", { nombre: usuario.nombre })}</DialogTitle></DialogHeader>
        <form onSubmit={guardar} className="space-y-4">
          <p className="text-sm text-ink-2">{t("Escribe la nueva contraseña. El usuario deberá cambiarla al ingresar.")}</p>
          <div><Label className="eyebrow">{t("Nueva contraseña *")}</Label><Input data-testid="reset-password-input" type="text" value={password} onChange={(e) => setPassword(e.target.value)} className="mt-1 h-11" required /></div>
          <DialogFooter className="gap-2">
            <Button type="button" variant="outline" onClick={onClose}>{t("Cancelar")}</Button>
            <Button type="submit" data-testid="reset-password-guardar" className="bg-brand hover:bg-brand-dark text-white" disabled={saving}>{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : t("Resetear")}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
